import { PreferenceElementWithCurrentValue } from "./preferenceConfigurationConst";

/**
 * Expected properties for defining a Preference Section Configuration (stem should match that provided when registering routes for a particular appId)
 */
export interface SectionConfiguration {
  stem: string;
  appId: string;
  title?: string;
  sectionId?: string;
  associatedStates?: string[];
  titleLocKey: string;
  preferenceElements?: PreferenceElementWithCurrentValue[];
  flightName?: string;
  automationId?: string;
}
/**
 * Key, value pair of name for preference option display text, value for it's actual value to be consumed in code
 */
export interface PreferenceOption {
  value: string;
  locData: PreferenceLocModel[];
  name?: string;
}

/** The locAppId should be specified if its value is not "ServiceDesk"*/
export interface PreferenceLocModel {
  locKey: string;
  locAppId?: string;
}

/**
 * @param {string} descriptionLocKey - text key for heading of select menu to display
 */
export interface BasePreferenceElementOptions {
  descriptionLocKey: string;
}

/**
 * @param {boolean} preventMidSessionUpdate - if true, will require a refresh to update the preference. i.e. true for localization, you wouldn't want some elements to be in a different locale than others.
 * @param {string} preferenceKey - corresponding preferenceKey for preferenceService sets/gets for particular preference
 * @param {any} initialValue - value only (no name) for which particular preference was initially set
 */
export interface BaseInteractablePreferenceElementOptions extends BasePreferenceElementOptions {
  preventMidSessionUpdate?: boolean;
  preferenceKey: string;
  initialValue: any;
}
/**
 * Specification of allowable/supported preference select menu control behaviors
 */
export type SelectMenuBehavior = "Sound" | "Font" | "FontColor";
/**
 * Base for preference element configs for preference ui
 */
export abstract class PreferenceElement implements BasePreferenceElementOptions {
  public description: string;
  public descriptionLocKey: string;
  public flightNameForElement: string;
  // @ngInject
  constructor(public name: string, baseOptions?: BasePreferenceElementOptions) {
    if (baseOptions) {
      this.descriptionLocKey = baseOptions.descriptionLocKey;
    }
  }

  public behindFlight(flightName: string): PreferenceElement {
    this.flightNameForElement = flightName;
    return this;
  }
}

/**
 * Base class for interactable preference elements.
 */
export abstract class InteractablePreferenceElement
  extends PreferenceElement
  implements BaseInteractablePreferenceElementOptions
{
  public preventMidSessionUpdate: boolean;
  public preferenceKey: string;
  public initialValue: any;
  // @ngInject
  constructor(name: string, options: BaseInteractablePreferenceElementOptions) {
    super(name, options);
    this.preventMidSessionUpdate = options.preventMidSessionUpdate;
    this.preferenceKey = options.preferenceKey;
    this.initialValue = options.initialValue;
  }
}

/**
 * Heading preference element config
 */
export class Heading extends PreferenceElement {
  /**
   * @param {string} descriptionLocKey - text key for heading to display
   */
  constructor(public descriptionLocKey: string) {
    super("Heading", { descriptionLocKey: descriptionLocKey });
  }
}
/**
 * Horizontal Ruler preference element config for showing an hr on the preference page
 */
export class HorizontalRuler extends PreferenceElement {
  constructor() {
    super("HorizontalRuler");
  }
}
/**
 * Section break preference element config for formatting after a section within section on preference page has been completed
 */
export class SectionBreak extends PreferenceElement {
  constructor() {
    super("SectionBreak");
  }
}

/**
 * Label preference element config
 */
export class Label extends PreferenceElement {
  /**
   * @param {string} descriptionLocKey - text key for label to display
   */
  constructor(public descriptionLocKey: string) {
    super("Label", { descriptionLocKey: descriptionLocKey });
  }
}

/**
 * Custom element to be shown for one off settings.
 */
export class CustomElement extends PreferenceElement {
  /**
   * @param {string} html - html element to be rendered
   */
  constructor(public html: string) {
    super("CustomElement");
  }
}

/**
 * @param {PreferenceOption[]} preferenceOptions - all of the potential options for which the consumer can select for particular preference
 * @param {SelectMenuBehavior} selectMenuBehavior - if select menu preference control is supposed to exhibit a specific behavior within the control (play sound, show specially formatted text etc.)
 * @param {sortOptions} sortOptions - if true, will sort the options in the select in alphabetical ascending order based on their localized values.
 */
export interface SelectMenuPreferenceOptions extends BaseInteractablePreferenceElementOptions {
  preferenceOptions: PreferenceOption[];
  selectMenuBehavior?: SelectMenuBehavior;
  sortOptions?: boolean;
}

/**
 * Select menu preference element config
 */
export class PreferenceSelectMenu extends InteractablePreferenceElement {
  public preferenceOptions: PreferenceOption[];
  public selectMenuBehavior: SelectMenuBehavior;
  public sortOptions: boolean;
  constructor(options: SelectMenuPreferenceOptions) {
    super("PreferenceSelectMenu", options);
    this.preferenceOptions = options.preferenceOptions;
    this.selectMenuBehavior = options.selectMenuBehavior;
    this.sortOptions = options.sortOptions;
  }
}
/**
 * Toggle preference element config
 */
export class PreferenceToggle extends InteractablePreferenceElement {
  constructor(options: BaseInteractablePreferenceElementOptions) {
    super("PreferenceToggle", options);
  }
}

/**
 * Button preference element config
 */
export class DeleteAllButton extends PreferenceElement {
  constructor(options: BasePreferenceElementOptions) {
    super("DeleteAllButton", options);
  }
}

/**
 * Localized link preference element config
 */
export class LocalizedLink extends PreferenceElement {
  public href: string;
  constructor(options: BasePreferenceElementOptions, href: string) {
    super("LocalizedLink", options);
    this.href = href;
  }
}

// tslint:disable-next-line:variable-name
export let Sound: SelectMenuBehavior = "Sound";
// tslint:disable-next-line:variable-name
export let Font: SelectMenuBehavior = "Font";
// tslint:disable-next-line:variable-name
export let FontColor: SelectMenuBehavior = "FontColor";
